import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, GraduationCap, Users, ArrowRight, Sparkles } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import Price from '../components/payments/Price';

const HomePerfect = () => {
  const t = useT();
  return (
    <div className="bg-[#FAF7F0]">
      {/* Hero */}
      <section className="relative bg-[#FAF7F0] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(26,47,74,0.03) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(212,175,55,0.04) 0%, transparent 60%)' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-[#D4AF37]/[0.06]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#1A2F4A]/[0.04]"></div>
        </div>

        <div className="max-w-[860px] mx-auto px-8 pt-28 pb-24 relative z-10">
          <div className="text-center">
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#1A2F4A] tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif", lineHeight: '1.08' }}
              data-testid="hero-headline">
              Parlez. Profitez. Progressez.
            </h1>
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="h-px w-14 bg-[#D4AF37]/60"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/60"></div>
              <div className="h-px w-14 bg-[#D4AF37]/60"></div>
            </div>
            <p
              className="text-sm md:text-[15px] text-[#4A4A4A] max-w-2xl mx-auto leading-relaxed mb-12"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
              data-testid="hero-tagline">
              {t({
                fr: <>Apprenez à parler français étape par étape avec notre Speaking Lab basé sur le CECR.<br className="hidden sm:block" />Vraies conversations. Progression structurée. Expression orale claire et confiante.</>,
                en: <>Learn to speak French step-by-step with our CEFR-based Speaking Lab.<br className="hidden sm:block" />Real conversations. Structured progression. Confident speaking with clarity.</>,
              })}
            </p>
            <div className="flex justify-center mb-8">
              <Link
                to="/speaking-lab"
                className="bg-[#1A2F4A] hover:bg-[#243a58] text-white px-12 py-4 rounded-full text-base font-semibold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2.5 group"
                data-testid="cta-start-journey">
                {t({ fr: 'Commencer votre parcours', en: 'Start Your Journey' })}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <p className="text-sm text-[#6B4E37]/70 tracking-wide" data-testid="hero-trust-tagline">
              {t({ fr: "Conçu pour les apprenants sérieux qui veulent parler — pas seulement étudier.", en: 'Designed for serious learners who want to speak—not just study.' })}
            </p>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"></div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="py-16 bg-[#FAFAF8]" data-testid="credibility-strip">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            <Pillar
              testid="pillar-spoken-french"
              iconPath="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              title={t({ fr: 'Français parlé authentique', en: 'Real Spoken French' })}
              desc={t({ fr: 'Conversation en direct, sans dialogues scriptés', en: 'Live conversation, not scripted dialogues' })} />
            <Pillar
              testid="pillar-cefr"
              iconPath="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
              title={t({ fr: 'Apprentissage aligné CECR', en: 'CEFR-Aligned Learning' })}
              desc={t({ fr: 'De A1 à natif, progression structurée', en: 'A1 to Native, structured progression' })} />
            <Pillar
              testid="pillar-educators"
              iconPath="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              title={t({ fr: 'Enseignants de français formés', en: 'Trained French Educators' })}
              desc={t({ fr: 'Spécialistes académiques et de la conversation', en: 'Academic & conversation specialists' })} />
            <Pillar
              testid="pillar-culture"
              iconPath="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              title={t({ fr: 'Langue + Culture', en: 'Language + Culture' })}
              desc={t({ fr: 'Parler français avec confiance et contexte', en: 'Speak French with confidence and context' })} />
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="py-20 bg-white" data-testid="positioning-section">
        <div className="max-w-[800px] mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A2F4A] mb-8" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}>
            {t({ fr: 'Conçu pour les apprenants de français sérieux', en: 'Designed for serious French learners' })}
          </h2>
          <p className="text-lg md:text-xl text-[#4A4A4A] leading-relaxed font-light">
            {t({
              fr: "Selora s'adresse aux étudiants, professionnels et candidats aux examens qui veulent parler français naturellement, progresser par niveau et gagner une véritable confiance conversationnelle — au-delà des applications et des traductions.",
              en: "Selora is for students, professionals, and exam-oriented learners who want to speak French naturally, progress by level, and gain real conversational confidence — beyond apps and translations.",
            })}
          </p>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-20 bg-[#FAF7F0]">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t({ fr: 'Nos programmes', en: 'Our Programs' })}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-20 bg-[#D4AF37]"></div>
              <span className="text-[#D4AF37]">⚜</span>
              <div className="h-px w-20 bg-[#D4AF37]"></div>
            </div>
            <p className="text-lg text-[#6B4E37]">
              {t({ fr: "Parcours d'éducation française complets", en: 'Comprehensive French education pathways' })}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ProgramCard to="/lire-le-francais" testid="feature-literature" Icon={BookOpen}
              title="Lire le Français"
              desc={t({ fr: 'Histoires, poésie & voix francophones', en: 'Stories, poetry & francophone voices' })} />
            <ProgramCard to="/les-mots-en-voyage" testid="feature-translation" Icon={FileText}
              title="Les Mots en Voyage"
              desc={t({ fr: "Voyagez d'une langue à l'autre", en: 'Journey from one language to another' })} />
            <ProgramCard to="/dashboard" testid="feature-student" Icon={GraduationCap}
              title={t({ fr: 'Espace Étudiant', en: 'Student Portal' })}
              desc={t({ fr: 'Accédez à votre tableau de bord', en: 'Access your learning dashboard' })} />
            <ProgramCard to="/teachers-portal" testid="feature-teacher" Icon={Users}
              title={t({ fr: 'Espace Enseignants', en: 'Teachers Portal' })}
              desc={t({ fr: 'Ressources & outils pour éducateurs', en: 'Educator resources & tools' })} />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE SELORA */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t({ fr: 'Pourquoi choisir Selora', en: 'Why Choose Selora' })}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-20 bg-[#D4AF37]"></div>
              <span className="text-[#D4AF37]">⚜</span>
              <div className="h-px w-20 bg-[#D4AF37]"></div>
            </div>
            <p className="text-lg text-[#6B4E37]">
              {t({ fr: 'Vivez un apprentissage authentique du français', en: 'Experience authentic French learning' })}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <WhyCard testid="why-feature-1"
              iconPath="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              title={t({ fr: 'Programme expert', en: 'Expert Curriculum' })}
              desc={t({ fr: 'Cours structurés conçus par des enseignants certifiés selon le CECR.', en: 'Structured courses designed by certified French educators following CEFR standards.' })} />
            <WhyCard testid="why-feature-2"
              iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              title={t({ fr: 'Enseignants natifs', en: 'Native Instructors' })}
              desc={t({ fr: "Apprenez avec des francophones natifs riches d'années d'expérience.", en: 'Learn from native French speakers with years of teaching experience.' })} />
            <WhyCard testid="why-feature-3"
              iconPath="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              title={t({ fr: 'Pratique assistée par IA', en: 'AI-Powered Practice' })}
              desc={t({ fr: "Feedback avancé sur la prononciation et parcours d'apprentissage personnalisés.", en: 'Advanced pronunciation feedback and personalized learning paths.' })} />
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-20 bg-[#FAF7F0]" data-testid="home-pricing-preview">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="text-center mb-12">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.3em] font-bold mb-2">
              {t({ fr: 'Formules', en: 'Plans' })}
            </p>
            <h2 className="text-4xl font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t({ fr: 'Un tarif pour chaque parcours', en: 'A plan for every journey' })}
            </h2>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-16 bg-[#D4AF37]"></div>
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <div className="h-px w-16 bg-[#D4AF37]"></div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <HomePriceCard
              testid="home-price-teacher"
              eyebrow={t({ fr: 'À la carte', en: 'Pay as you go' })}
              title={t({ fr: 'Réserver un professeur', en: 'Book a Teacher' })}
              priceEur={20}
              suffix={t({ fr: ' / heure', en: ' / hour' })} />
            <HomePriceCard
              testid="home-price-membership"
              highlight
              eyebrow={t({ fr: 'Recommandé', en: 'Recommended' })}
              title={t({ fr: 'Adhésion Selora', en: 'Selora Membership' })}
              priceEur={50}
              suffix={t({ fr: ' · paiement unique', en: ' · one-time' })} />
            <HomePriceCard
              testid="home-price-club"
              eyebrow={t({ fr: 'Communauté', en: 'Community' })}
              title="Le Club Francophone"
              priceEur={5}
              suffix={t({ fr: ' / mois', en: ' / month' })} />
          </div>

          <div className="text-center mt-8">
            <Link
              to="/pricing"
              data-testid="home-pricing-cta"
              className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white px-8 py-3.5 rounded-full text-sm font-semibold shadow-md hover:shadow-lg transition-all">
              {t({ fr: 'Voir toutes les formules', en: 'View all plans' })}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A2F4A]">
        <div className="max-w-[900px] mx-auto px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t({ fr: 'Commencez votre parcours en français aujourd\'hui', en: 'Begin Your French Journey Today' })}
          </h2>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-[#D4AF37]"></div>
            <span className="text-[#D4AF37] text-xl">⚜</span>
            <div className="h-px w-16 bg-[#D4AF37]"></div>
          </div>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            {t({
              fr: "Rejoignez des milliers d'apprenants qui ont maîtrisé le français grâce au programme complet et à l'expertise de Selora.",
              en: "Join thousands of students who have mastered French with Selora's comprehensive curriculum and expert guidance.",
            })}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-10 py-4 rounded-lg text-lg font-bold shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-get-started">
              {t({ fr: 'Commencer gratuitement', en: 'Get Started Free' })}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FAF7F0] border-t border-[#D4AF37]/30 py-12">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-[#D4AF37] shadow flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_parlezmoifrench/artifacts/25ispvfj_selora%20logo-removebg.jpg"
                  alt="Selora Logo"
                  className="w-14 h-14 object-cover scale-125"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  SELORA
                </span>
                <p className="text-xs text-[#6B4E37]">French Academy</p>
              </div>
            </div>
            <p className="text-[#6B4E37] text-sm" data-testid="footer-copyright">
              {t({ fr: '© 2026 Selora French Academy. Tous droits réservés.', en: '© 2026 Selora French Academy. All rights reserved.' })}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function Pillar({ testid, iconPath, title, desc }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="mb-5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[#D4AF37]/40">
          <svg className="w-7 h-7 text-[#1A2F4A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.25}>
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        </div>
      </div>
      <h3 className="text-lg font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <p className="text-[#6B4E37] text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function ProgramCard({ to, testid, Icon, title, desc }) {
  return (
    <Link to={to} className="bg-white border border-[#D4AF37]/30 rounded-xl p-6 text-center hover:shadow-lg hover:border-[#D4AF37] transition-all group" data-testid={testid}>
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FAF7F0] rounded-xl group-hover:bg-[#D4AF37]/10 transition-colors">
          <Icon className="w-8 h-8 text-[#1A2F4A]" strokeWidth={1.5} />
        </div>
      </div>
      <h3 className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <p className="text-sm text-[#6B4E37] mt-2">{desc}</p>
    </Link>
  );
}

function WhyCard({ testid, iconPath, title, desc }) {
  return (
    <div className="text-center p-8" data-testid={testid}>
      <div className="w-20 h-20 mx-auto bg-[#1A2F4A] rounded-full flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={iconPath} />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <p className="text-[#6B4E37] leading-relaxed">{desc}</p>
    </div>
  );
}

function HomePriceCard({ testid, eyebrow, title, priceEur, suffix, highlight }) {
  return (
    <Link
      to="/pricing"
      data-testid={testid}
      className={`bg-white rounded-2xl px-6 py-6 border-2 flex flex-col text-left transition-all hover:shadow-md ${highlight ? 'border-[#D4AF37] shadow-lg' : 'border-[#E8E4DC] hover:border-[#D4AF37]/70'}`}>
      <p className="text-[9px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1">{eyebrow}</p>
      <h3 className="text-base font-bold text-[#1A2F4A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <Price basePriceEUR={priceEur} suffix={suffix} size="lg" showBase testid={`${testid}-price`} />
      <div className="mt-4 pt-3 border-t border-[#F3F4F6] text-[11px] text-[#1A2F4A] font-semibold flex items-center gap-1">
        <span>Voir les détails</span>
        <ArrowRight className="w-3 h-3 text-[#D4AF37]" />
      </div>
    </Link>
  );
}

export default HomePerfect;
